import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface FABContextType {
  isFABVisible: boolean;
  setFABVisible: (visible: boolean) => void;
  toggleFAB: () => void;
}

const FABContext = createContext<FABContextType | undefined>(undefined);

interface FABProviderProps {
  children: ReactNode;
}

export const FABProvider: React.FC<FABProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // Initialize from localStorage, default to true (but false for customer users)
  const [isFABVisible, setIsFABVisible] = useState(() => {
    // Customer userek számára mindig false
    if (user?.role === 'customer') {
      return false;
    }
    
    try {
      const stored = localStorage.getItem('fab-visible');
      return stored !== null ? JSON.parse(stored) : true;
    } catch {
      return true;
    }
  });

  // Save to localStorage whenever the value changes (except for customer users)
  useEffect(() => {
    if (user?.role === 'customer') {
      return; // Ne mentse el customer userek számára
    }
    
    try {
      localStorage.setItem('fab-visible', JSON.stringify(isFABVisible));
    } catch (error) {
      console.warn('Failed to save FAB visibility to localStorage:', error);
    }
  }, [isFABVisible, user?.role]);

  // Automatikusan elrejti a FAB-ot customer userek számára
  useEffect(() => {
    if (user?.role === 'customer' && isFABVisible) {
      setIsFABVisible(false);
    }
  }, [user?.role]);

  const setFABVisible = (visible: boolean) => {
    setIsFABVisible(visible);
  };

  const toggleFAB = () => {
    setIsFABVisible((prev: boolean) => !prev);
  };

  return (
    <FABContext.Provider value={{
      isFABVisible,
      setFABVisible,
      toggleFAB
    }}>
      {children}
    </FABContext.Provider>
  );
};

export const useFAB = (): FABContextType => {
  const context = useContext(FABContext);
  if (context === undefined) {
    throw new Error('useFAB must be used within a FABProvider');
  }
  return context;
};
